/**
 * Initialize the canvas for drawing.
 *
 * @param {Object} dom - The DOM elements for the canvas module.
 * @param {Object} state - The state object for the canvas module.
 * @param {Function} checkCanvasHasContent - Function to check if the canvas has content.
 *
 * @returns {void}
 */
export default function initCanvas({
	dom,
	state,
	checkCanvasHasContent,
	pushState,
	deactivateCursorAccessory,
}) {
	const { canvas, accessoryContainer, startPoint, wrapper } = dom;
	if (!canvas) return;

	const ctx = canvas.getContext('2d');
	const tileImages = {};

	/**
	 * Helper to load and cache an image from a URL.
	 */
	const loadTileImage = (url) => {
		if (!url || tileImages[url]) return; // Already loaded or empty
		const img = new Image();
		img.src = url;
		tileImages[url] = img;
	};

	const tile1Url = wrapper.getAttribute('data-drawing-tile');
	const tile2Url = wrapper.getAttribute('data-second-drawing-tile');

	loadTileImage(tile1Url);
	loadTileImage(tile2Url);

	const maxBrushSize = 60;
	const minBrushSize = 10;
	let isDrawing = false;
	let isColoring = false;

	/**
	 * Resize the canvas and accessory container.
	 */
	const resizeCanvas = () => {
		const container = canvas.parentElement;
		if (container.clientHeight < window.innerHeight) {
			container.style.height = `${window.innerHeight}px`;
		}
		canvas.width = container.clientWidth;
		canvas.height = container.clientHeight;
		accessoryContainer.style.width = `${canvas.width}px`;
		accessoryContainer.style.height = `${canvas.height}px`;
		ctx.clearRect(0, 0, canvas.width, canvas.height);
	};

	/**
	 * Check if there is hair at the given point on the canvas.
	 */
	const isHairAtPoint = (x, y) => {
		const p = ctx.getImageData(x, y, 1, 1).data;
		return p[3] > 0; // Check alpha channel
	};

	/**
	 * Draw on the canvas.
	 */
	const draw = ({ offsetX, offsetY, movementX, movementY }) => {
		const activeUrl = state.drawingTile;
		const currentImage = tileImages[activeUrl];

		if (!currentImage || !currentImage.complete || currentImage.naturalWidth === 0) {
			return;
		}

		const distances = [offsetX, canvas.width - offsetX, offsetY, canvas.height - offsetY];
		const minDist = Math.min(...distances);
		const brushSize =
			minDist < 100 ? minBrushSize + (minDist / 100) * (maxBrushSize - minBrushSize) : maxBrushSize;
		const x = offsetX - brushSize / 2;
		const y = offsetY - brushSize / 2;
		const angle = Math.atan2(movementY, movementX);

		[1, -1].forEach((scale) => {
			ctx.save();
			ctx.translate(
				scale === 1 ? x + brushSize / 2 : canvas.width - x - brushSize / 2,
				y + brushSize / 2,
			);
			ctx.rotate(angle);
			if (scale === -1) ctx.scale(-1, 1);

			ctx.drawImage(currentImage, -brushSize / 2, -brushSize / 2, brushSize, brushSize);
			ctx.restore();
		});
	};

	/**
	 * Fill the area around the pointer.
	 */
	const fillArea = (e) => {
		if (!isHairAtPoint(e.offsetX, e.offsetY)) return;

		const airbrushRadius = 75;
		const airbrushAlpha = 0.15;

		const tempCtx = document.createElement('canvas').getContext('2d');
		tempCtx.fillStyle = state.drawingColor;
		tempCtx.fillRect(0, 0, 1, 1);
		const [r, g, b] = tempCtx.getImageData(0, 0, 1, 1).data;
		const transparentColor = `rgba(${r}, ${g}, ${b}, 0)`;

		const offscreenCanvas = document.createElement('canvas');
		offscreenCanvas.width = canvas.width;
		offscreenCanvas.height = canvas.height;
		const octx = offscreenCanvas.getContext('2d');

		const grad = octx.createRadialGradient(
			e.offsetX,
			e.offsetY,
			0,
			e.offsetX,
			e.offsetY,
			airbrushRadius,
		);
		grad.addColorStop(0, state.drawingColor);
		grad.addColorStop(1, transparentColor);

		octx.fillStyle = grad;
		octx.globalAlpha = airbrushAlpha;

		octx.beginPath();
		octx.arc(e.offsetX, e.offsetY, airbrushRadius, 0, Math.PI * 2);
		octx.fill();

		octx.globalCompositeOperation = 'destination-in';
		octx.drawImage(canvas, 0, 0);

		ctx.drawImage(offscreenCanvas, 0, 0);
	};

	/**
	 * Place an accessory on the canvas.
	 */
	const placeAccessory = (e) => {
		if (!isHairAtPoint(e.offsetX, e.offsetY)) return;
		pushState();

		const wRect = accessoryContainer.getBoundingClientRect();
		const relX = e.clientX - wRect.left;
		const relY = e.clientY - wRect.top;

		const imgEl = new Image();
		imgEl.draggable = false;
		imgEl.src = state.selectedAccessory;
		imgEl.setAttribute('data-placed-accessory', 'true');
		imgEl.classList.add('wp-block-mavis-design-a-hairstyle__canvas-accessory');

		const cursorAcc = document.querySelector(
			'.wp-block-mavis-design-a-hairstyle__cursor-accessory',
		);
		if (cursorAcc?.classList.contains('flipped')) {
			imgEl.classList.add('flipped');
		}

		Object.assign(imgEl.style, {
			position: 'absolute',
			width: `${state.currentAccessorySize}px`,
			height: 'auto',
			zIndex: '3',
		});
		accessoryContainer.appendChild(imgEl);

		/**
		 * Position the accessory once the image has loaded.
		 */
		imgEl.onload = () => {
			imgEl.style.left = `${relX - imgEl.offsetWidth / 2}px`;
			imgEl.style.top = `${relY - imgEl.offsetHeight / 2}px`;
			checkCanvasHasContent();
		};

		deactivateCursorAccessory();
		// eslint-disable-next-line no-param-reassign
		state.selectedAccessory = null;
	};

	/**
	 * Handle pointer move events for drawing.
	 * @param {PointerEvent} evt - The pointer event.
	 * @returns {void}
	 */
	const onPointerMoveDraw = (evt) => {
		if (!isDrawing) return;
		const rect = canvas.getBoundingClientRect();
		draw({
			offsetX: evt.clientX - rect.left,
			offsetY: evt.clientY - rect.top,
			movementX: evt.movementX,
			movementY: evt.movementY,
		});
	};

	/**
	 * Handle pointer up events to stop drawing.
	 *
	 * @returns {void}
	 */
	const onPointerUpDraw = () => {
		isDrawing = false;
		document.removeEventListener('pointermove', onPointerMoveDraw);
		document.removeEventListener('pointerup', onPointerUpDraw);
		checkCanvasHasContent();
	};

	/**
	 * Handle pointer down events to start drawing or coloring.
	 * @param {PointerEvent} e - The pointer event.
	 * @returns {void}
	 */
	const onPointerDown = (e) => {
		if (state.currentMode === 'drawing') {
			const onStart = startPoint.contains(e.target);
			const onHair = isHairAtPoint(e.offsetX, e.offsetY);
			if (!onStart && !onHair) return;
			if (!state.drawingTile || !tileImages[state.drawingTile]) return;

			pushState();
			isDrawing = true;
			const rect = canvas.getBoundingClientRect();
			draw({
				offsetX: e.clientX - rect.left,
				offsetY: e.clientY - rect.top,
				movementX: 0,
				movementY: 0,
			});
			document.addEventListener('pointermove', onPointerMoveDraw);
			document.addEventListener('pointerup', onPointerUpDraw);
		} else if (state.currentMode === 'coloring') {
			if (!isHairAtPoint(e.offsetX, e.offsetY)) return;
			pushState();
			isColoring = true;
			fillArea(e);
			canvas.setPointerCapture(e.pointerId);
		}
	};

	if (startPoint && canvas) {
		startPoint.addEventListener('pointerdown', onPointerDown);
		canvas.addEventListener('pointerdown', onPointerDown);

		canvas.addEventListener('click', (e) => {
			if (state.currentMode === 'accessorizing' && state.selectedAccessory) {
				placeAccessory(e);
			} else if (state.currentMode === 'coloring') {
				fillArea(e);
			}
		});

		canvas.addEventListener('pointermove', (e) => {
			if (!isColoring || state.currentMode !== 'coloring' || !isHairAtPoint(e.offsetX, e.offsetY))
				return;
			fillArea(e);
		});

		/**
		 * Stop coloring when pointer is released or canceled.
		 *
		 * @returns {void}
		 */
		const stopColoring = () => {
			isColoring = false;
		};
		canvas.addEventListener('pointerup', stopColoring);
		canvas.addEventListener('pointercancel', stopColoring);
	}

	window.addEventListener('load', resizeCanvas);
	resizeCanvas();
}
